import { Controller } from "@hotwired/stimulus"

// Universal Job Status Button Controller  
// Handles button states and progress tracking for any Sidekiq job
export default class extends Controller {
  static targets = ["submitButton"]
  static values = { 
    type: String,
    status: String,
    checkInterval: { type: Number, default: 5000 } // Check every 5 seconds
  }

  connect() {
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    this.pollingInterval = setInterval(() => {
      this.checkJobStatus()
    }, this.checkIntervalValue)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkJobStatus() {
    try {
      // Build the job status URL based on current path
      const jobStatusUrl = `${window.location.pathname}/job_status`
      
      const response = await fetch(jobStatusUrl, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin' // Include cookies for session
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      if (data.status === 'completed') {
        this.stopPolling()
        window.location.reload()
      } else if (data.status === 'failed') {
        this.stopPolling()
        // Show notification instead of replacing button
        this.showNotification(data.message || 'Job failed.', 'error')
        // Refresh page to reset button state
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else if (data.status === 'retrying') {
        // Show warning notification but keep polling
        this.showNotification(data.message || 'Job encountered an error and is retrying...', 'warning')
      } else if (data.status === 'unknown') {
        // No job to track, stop polling
        this.stopPolling()
        window.location.reload()
      }
      // If status is 'running' or 'retrying', continue polling
      
      // Stop polling after reasonable time even if no clear completion/failure signal
      if (!this.pollStartTime) {
        this.pollStartTime = Date.now()
      }
      
      // Stop polling after 5 minutes (300000ms) to prevent infinite polling
      if (Date.now() - this.pollStartTime > 300000) {
        this.stopPolling()
        this.showNotification('Job is taking longer than expected. Please refresh the page to check status.', 'error')
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      }
      
    } catch (error) {
      // Don't stop polling on network errors, just continue silently
    }
  }

  async startJob(event) {
    event.preventDefault()
    
    // Show immediate progress feedback - only replace this component, not parent container
    this.element.innerHTML = this.generateProgressHTML()
    
    // Submit the form via fetch to avoid page refresh
    const form = event.target.closest('form')
    if (!form) return false
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    
    try {
      const response = await fetch(form.action, {
        method: form.method || 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      if (data.job_started) {
        // Job started successfully, begin polling
        this.pollStartTime = Date.now() // Reset poll timer
        this.startPolling()
      } else {
        // Handle case where job didn't start
        this.showNotification(data.error || 'Failed to start job', 'error')
        // Reset button state
        this.element.innerHTML = this.generateProgressHTML().replace('Generating', 'Failed to start')
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
      
    } catch (error) {
      this.showNotification(error.message, 'error')
      // Reset button state
      this.element.innerHTML = this.generateProgressHTML().replace('Generating', 'Error')
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
    
    return false
  }

  generateProgressHTML() {
    const jobName = this.typeValue === 'engagement_summary' ? 'summary' : 'brain diagram'
    
    return `
      <div class="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 min-h-[44px]">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Generating ${jobName}...
      </div>
    `
  }

  
  showNotification(message, type = 'error') {
    // Create flash container if it doesn't exist
    let flashContainer = document.getElementById('flash')
    if (!flashContainer) {
      flashContainer = document.createElement('div')
      flashContainer.id = 'flash'
      flashContainer.className = 'mx-auto'
      flashContainer.setAttribute('data-controller', 'flash')
      document.body.insertBefore(flashContainer, document.body.firstChild)
    }
    
    // Create notification element
    const notification = document.createElement('div')
    notification.className = 'absolute z-50 px-4 py-2 transform -translate-x-1/2 border rounded-md shadow-2xl bg-blue-50 top-1/2 left-1/2 top-4 border-slate-400'
    
    const iconSvg = type === 'error' 
      ? '<svg class="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" /></svg>'
      : '<svg class="w-6 h-6 text-blue-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clip-rule="evenodd" /></svg>'
    
    notification.innerHTML = `
      <div class="flex items-center gap-2">
        <div class="">
          ${iconSvg}
        </div>
        <div class="flex-1 text-gray-700">
          ${message}
        </div>
        <div class="ml-4 text-xs cursor-pointer opacity-20 hover:opacity-50" onclick="this.closest('div[class*=absolute]').remove()">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      </div>
    `
    
    // Add to flash container
    flashContainer.appendChild(notification)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}